import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
    Anchor,
    Body2,
    Caption,
    FilledCard,
    RichText,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import { isInsuranceOfferType } from '@phx/data-sources/medication-marketplace';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import { useDrugKey } from '../../../../hooks/use-drug-key';
import { useMedicationDisclaimers } from '../../../../hooks/use-medication-disclosures';
import { usePharmacyName } from '../../../../hooks/use-pharmacy-name';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import type { PricingCardProps } from '../common/shared';
import { usePriceQualifier } from '../common/use-price-qualifier';

import { useInsurancePricing } from './use-insurance-pricing';

export const InsurancePricing: FC<PricingCardProps> = ({ offer }) => {
    const { t } = useTranslation('lilly');
    const { allOffers, medication } = useFillPrescription();
    const { subPathway } = useFillPathway();

    const drugKey = useDrugKey(medication);
    const terms = useProgramTerms(medication, 'insurance');

    const insuranceOffers = allOffers.filter((offer) =>
        isInsuranceOfferType(offer.headlinePriceType)
    );

    const hasSavingsCard = insuranceOffers.some(
        (offer) => offer.headlinePriceType === 'CopayAssistance'
    );

    const isRetail = subPathway === 'retail';

    const hasPricing =
        !isRetail &&
        insuranceOffers.some(
            (offer) =>
                offer.headlinePriceType === 'CopayAssistance' ||
                offer.headlinePriceType === 'Retail' ||
                offer.headlinePriceType === 'NonCoveredBenefit'
        );

    const { patientPay, priceType } = useInsurancePricing(offer);
    const priceQualifier = usePriceQualifier({
        medication,
        patientPay,
        priceType,
    });

    const pharmacyName = usePharmacyName(
        offer?.provider ?? insuranceOffers[0]?.provider
    );

    const disclaimer =
        pharmacyName && (!!offer || insuranceOffers.length === 1)
            ? t('fill.pricing.feesMayApplyWithPharmacy', {
                  pharmacyName,
                  termsDisclaimer: '',
              })
            : t('fill.pricing.feesMayApply', { termsDisclaimer: '' });

    const richDisclaimers = useMedicationDisclaimers({
        headlinePriceType:
            offer?.headlinePriceType ??
            (hasSavingsCard ? 'CopayAssistance' : null),
        medication,
        slot: 'price-card',
    });

    const showRichDisclaimer = !!richDisclaimers.length;

    return (
        <>
            <FilledCard
                withBorder
                color="primary"
                data-testid="pharmacy-pricing-card"
            >
                <Stack gap="xxxs">
                    {!hasPricing ||
                    offer?.headlinePriceType === 'InsuredPriceAtPharmacy' ? (
                        <Subtitle2>
                            {t('fill.pricing.finalPriceAtPharmacy')}
                        </Subtitle2>
                    ) : (
                        <>
                            {terms?.savingsCard ? (
                                <Subtitle2>
                                    {t('fill.insurance.pricing.withCard', {
                                        cardName: terms.savingsCard.name,
                                    })}
                                </Subtitle2>
                            ) : hasSavingsCard ? (
                                <Subtitle2>
                                    {t('fill.insurance.pricing.withInsurance')}
                                </Subtitle2>
                            ) : null}
                            <Subtitle1>
                                {t('fill.pricing.asLowAs', {
                                    price: patientPay,
                                    qualifier: priceQualifier?.text ?? '',
                                })}
                            </Subtitle1>
                        </>
                    )}
                    {priceQualifier?.description ? (
                        <RichText {...priceQualifier.description} />
                    ) : null}
                </Stack>
            </FilledCard>
            <Stack gap={0} data-testid="pharmacy-pricing-terms-and-conditions">
                {!isRetail && hasSavingsCard && !showRichDisclaimer ? (
                    <Caption>{disclaimer}</Caption>
                ) : null}
                {!isRetail && showRichDisclaimer
                    ? richDisclaimers.map((richDisclaimer) => {
                          const richContent = richDisclaimer.content;

                          return richContent.map(({ key, ...richLine }) => (
                              <RichText key={key} {...richLine} />
                          ));
                      })
                    : null}
                {drugKey && terms ? (
                    <Body2>
                        <Trans
                            i18nKey="fill.pricing.seeTerms"
                            components={{
                                1: (
                                    <Anchor
                                        href={getAbsoluteRoute(
                                            'programs.drugKey.terms.pathway',
                                            { drugKey, pathway: 'insurance' }
                                        )}
                                    />
                                ),
                            }}
                        />
                    </Body2>
                ) : null}
            </Stack>
        </>
    );
};
